import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatToolbarModule } from '@angular/material/toolbar';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { AuthenticationFacade, MenuStage, NavigationLink, NavigationLinkRegistryService } from '@ebeihilfe/core';
import { TranslateModule } from '@ngx-translate/core';
import { from, mergeMap, Observable, ReplaySubject, shareReplay } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { FormatRouteService } from '../../services/format-route/format-route.service';
import { AccountMenuComponent } from './menu/account-menu.component';
import { ResponsiveFilterPipe } from './responsive-filter/responsive-filter.pipe';

@Component({
  imports: [
    AccountMenuComponent,
    AsyncPipe,
    MatButtonModule,
    MatMenuModule,
    MatIconModule,
    MatToolbarModule,
    TranslateModule,
    RouterLink,
    RouterLinkActive,
    ResponsiveFilterPipe,
  ],
  selector: 'portal-navigation',
  templateUrl: './portal-toolbar.component.html',
  styleUrls: ['./portal-toolbar.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PortalToolbarComponent implements OnInit {
  private readonly auth = inject(AuthenticationFacade);
  private readonly destroyRef = inject(DestroyRef);
  private readonly formatRouteService = inject(FormatRouteService);
  private readonly menuService = inject(NavigationLinkRegistryService);
  private readonly router = inject(Router);

  public isAuthenticated: Observable<boolean>;
  public menuItems: Observable<NavigationLink[]>;
  public skipLinkPath: string;

  public ngOnInit(): void {
    const menuItemsSubject = new ReplaySubject<NavigationLink[]>(1);
    this.isAuthenticated = this.auth.authenticationChange$;

    const menuItems = this.isAuthenticated.pipe(
      map((authenticated) => (authenticated ? MenuStage.SIGNED_IN : MenuStage.INIT)),
      map((stage) => this.menuService.getMenuItemsForStage(stage)),
      mergeMap((value) => from(value)),
      mergeMap((value) => value),
      shareReplay(1),
    );

    menuItems
      .pipe(
        map((items) =>
          items.map((item) => ({
            ...item,
            route: this.formatRouteService.formatRoute(item.route),
          })),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(menuItemsSubject);

    this.menuItems = menuItemsSubject.asObservable();

    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
      if (!this.router.url.endsWith('#main-content')) {
        this.skipLinkPath = `${this.router.url}#main-content`;
      }
    });
  }
}
